import { createSelector } from "reselect";
import * as R from "ramda";
import { getters as TabGetters } from "ui-kit/Tabs";
import { getters } from "EventLight/Sessions/AddSessionModal/model";
import * as FormSelectors from "ui-kit/Form/selectors";
import { isEventFeatureEnabled } from "redux/modules/event/selectors";
import { FEATURES } from "utils/event-types-and-feature-constants";
import {
  LOCATION_TYPE_IDS,
  LOCATION_TYPES,
  TABS_CONTENT,
  TABS,
  SESSION_MODAL_TABS_INSTANCE_ID,
  NEXT_BTN_WIDTH_FOR_TAB_CONTENT,
  NEXT_BTN_WIDTH
} from "EventLight/Sessions/AddSessionModal/constants";
import { canUserDo } from "redux/modules/permissions/user-permission-profile/selectors";
import { eventId as getEventId } from "redux/modules/event/selectors";
import * as STANDARD_MODULE_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";
import { eventDetails } from "redux/modules/event/selectors";

const getCanUserDo = R.memoizeWith(getEventId, state =>
  canUserDo(state, getEventId(state))
);

export const canEditSessions = createSelector(getCanUserDo, canDo => {
  return canDo(`${STANDARD_MODULE_IDS.sessions.id}_update`);
});

export const getCurrentTab = createSelector(
  state =>
    TabGetters.selectedTab(state, {
      instanceId: SESSION_MODAL_TABS_INSTANCE_ID
    }),
  tab => tab || TABS.BASIC_DETAILS
);

export const getTabsList = createSelector(
  getters.sessionId,
  getters.isSubsession,
  getCanUserDo,
  (sessionId, isSubsession, canDo) => {
    return TABS_CONTENT.map(tab => {
      let isVisibleByPermission = true;
      if (tab.id === TABS.CONTENT) {
        isVisibleByPermission = canDo(`${STANDARD_MODULE_IDS.content.id}_read`);
      }
      return {
        ...tab,
        visible:
          isVisibleByPermission &&
          (isSubsession && [TABS.OTHER, TABS.SUBSESSIONS].includes(tab.id)
            ? false
            : tab.id === TABS.SUBSESSIONS
            ? true
            : true),
        disabled:
          [
            TABS.CONTENT,
            TABS.MESSAGES,
            TABS.ACTIVITY,
            TABS.SUBSESSIONS
          ].includes(tab.id) && !sessionId,
        tooltip:
          [
            TABS.CONTENT,
            TABS.MESSAGES,
            TABS.ACTIVITY,
            TABS.SUBSESSIONS
          ].includes(tab.id) && !sessionId
            ? `You can access this after saving the ${
                isSubsession ? "subsession" : "session"
              }`
            : null
      };
    }).filter(tab => tab.visible);
  }
);

export const getDefaultTab = createSelector(getTabsList, tabList => {
  if (R.isEmpty(tabList[0].id)) {
    return TABS.BASIC_DETAILS;
  } else {
    return tabList[0].id;
  }
});

export const getNextBtnText = createSelector(
  getters.activeTab,
  getters.isSubsession,
  (activeTab, isSubsession) => {
    return isSubsession ? "Save Subsession" : "Save Session";
  }
);

export const isExpoEnabled = createSelector(isEventFeatureEnabled, isEnabled =>
  isEnabled(FEATURES.EXPO)
);

export const isBiddingEnabled = createSelector(
  isEventFeatureEnabled,
  isEnabled => isEnabled(FEATURES.BIDDING)
);

export const getLocationOptions = createSelector(
  isExpoEnabled,
  isBiddingEnabled,
  (isExpoEnabled, isBiddingEnabled) => {
    let typesToReturn = LOCATION_TYPES;
    if (!isExpoEnabled) {
      typesToReturn = typesToReturn.filter(
        t => t.id !== LOCATION_TYPE_IDS.BOOTH
      );
    }
    if (!isBiddingEnabled) {
      typesToReturn = typesToReturn.filter(
        t => t.id !== LOCATION_TYPE_IDS.BIDDING
      );
    }
    return typesToReturn;
  }
);

export const getIsValid = createSelector(getters.data, data => {
  if (!data.title || !data.title.length) {
    return false;
  }

  return true;
});

export const getIsSubSessionButton = createSelector(
  getters.sessionsToConvert,
  getters.selectedsessionsToConvert,
  (sessionsToConvert, selectedsessionsToConvert) =>
    !(sessionsToConvert.length && selectedsessionsToConvert.length)
);

export const getShowDraftBtn = createSelector(
  getters.activeTab,
  activeTab => activeTab === TABS.CONTENT
);

export const getShowNextIcon = createSelector(getters.activeTab, () => false);

export const getShowBackBtn = createSelector(getters.activeTab, () => false);

export const getNextBtnWidth = createSelector(getters.activeTab, activeTab => {
  if (activeTab === TABS.CONTENT) {
    return NEXT_BTN_WIDTH_FOR_TAB_CONTENT;
  }
  return NEXT_BTN_WIDTH;
});

export const getEditedField = R.compose(
  field => R.prop("value", field),
  FormSelectors.getField
);

export const getParentSessionName = createSelector(
  getters.parentSessionData,
  data => data.title
);

export const getSubsessions = createSelector(
  getters.data,
  data => data.subsessions
);

export const getTranslatedValuesForField = createSelector(
  getters.selectedTranslatedFieldId,
  getters.translatedValues,
  getters.data,
  eventDetails,
  (selectedTranslatedFieldId, translatedValues, data, eventDetails) => {
    const values = translatedValues.filter(
      v => v.field_internal_name === selectedTranslatedFieldId
    );
    if (values.length) {
      return [
        {
          ...values[0],
          translated_field_name: values[0].field_name,
          translated_value: data[values[0].field_internal_name],
          language: eventDetails.language,
          language_label: "English",
          is_default: true
        },
        ...values.map(v => ({
          ...v,
          is_default: false
        }))
      ];
    }
    return [];
  }
);

export const getFieldsThatHaveTranslations = createSelector(
  getters.translatedValues,
  translatedValues => {
    return translatedValues.reduce((a, b) => {
      a[b.field_internal_name] = true;
      return a;
    }, {});
  }
);
